"use client";

import React, { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import type {
  IFilter,
  ILocationData,
  ILocationSearchHotel,
  ILocationSearchItem,
  IRecentLocationSearch,
} from "@/src/shared/features/search/types";
import { searchService } from "@/src/shared/features/search/services";
import LocationSearchList from "@/src/shared/features/search/components/LocationSearchList";
import { cn } from "@/src/lib/utils";

const LOCATION_DENIED = "Turn on location access to find stays around you";

interface IDestinationModalProps {
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  setActiveSearchSection?: React.Dispatch<
    React.SetStateAction<"where" | "dates" | "guests" | null>
  >;
  /** Mobile: called with the filters the pick makes instead of moving on to the dates. */
  onDestinationSelect?: (next: IFilter) => void;
  /** Runs a whole search at once: a recent search whose dates are still ahead. */
  onSearch?: (next: IFilter) => void;
  isMobile?: boolean;
}

/**
 * The "Where" panel: the typed text (debounced) drives the Python location search;
 * picking an area or a hotel fills the destination and moves on to the dates.
 */
const SearchDestinationModal: React.FC<IDestinationModalProps> = ({
  filters,
  setFilters,
  setActiveSearchSection,
  onDestinationSelect,
  onSearch,
  isMobile,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const typed = searchService.isNearby(filters.destination?.name)
    ? ""
    : filters.destination?.name || "";
  const [term, setTerm] = useState(typed);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = setTimeout(() => setTerm(typed), 500);
    return () => clearTimeout(handler);
  }, [typed]);

  const apply = (next: IFilter) => {
    setFilters(next);
    if (onDestinationSelect) {
      onDestinationSelect(next);
      return;
    }
    setActiveSearchSection?.("dates");
  };

  const choose = (destination: ILocationData) =>
    apply({ ...filters, destination });

  const handleLocation = (place: ILocationSearchItem) => choose(place);
  const handleHotel = (hotel: ILocationSearchHotel) =>
    choose(searchService.hotelDestination(hotel));

  const handleRecent = (recent: IRecentLocationSearch) => {
    const next = searchService.recentToFilter(recent, new Date());
    if (next.checkIn && onSearch) {
      onSearch(next);
      return;
    }
    apply(next);
  };

  const handleNearby = () => {
    if (!navigator.geolocation) {
      enqueueSnackbar(LOCATION_DENIED, { variant: "error" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        choose({
          name: "Nearby",
          lat: coords.latitude,
          lng: coords.longitude,
          id: 0,
          name_bn: null,
          order: 0,
          tier_1: 1,
          tier_2: 5,
          within: 10,
        }),
      () => enqueueSnackbar(LOCATION_DENIED, { variant: "error" }),
    );
  };

  /** Prevent scroll from propagating */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Wheel handler: only prevent when at top and scrolling up OR at bottom and scrolling down.
    // Also do nothing if content is not scrollable to avoid blocking wheel events.
    const onWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const delta = e.deltaY;

      // If there's nothing to scroll, don't interfere.
      if (scrollHeight <= clientHeight) return;

      // At top and trying to scroll up (delta < 0) -> prevent body scroll
      if (scrollTop === 0 && delta < 0) {
        e.preventDefault();
        return;
      }

      // At bottom (allow small rounding) and trying to scroll down -> prevent body scroll
      if (scrollTop + clientHeight >= scrollHeight - 1 && delta > 0) {
        e.preventDefault();
        return;
      }
      // Otherwise allow the element to scroll normally.
    };

    // Touch handling for mobile devices to prevent body overscroll when touching inside the element
    let startY = 0;
    const onTouchStart = (ev: TouchEvent) => {
      const touch = ev.touches?.[0];
      if (ev.touches.length === 1 && touch) startY = touch.clientY;
    };

    const onTouchMove = (ev: TouchEvent) => {
      const touch = ev.touches?.[0];
      if (!touch || ev.touches.length !== 1) return;
      const currentY = touch.clientY;
      const diff = startY - currentY; // positive when scrolling up
      const { scrollTop, scrollHeight, clientHeight } = el;

      // If there's nothing to scroll, allow default (don't block)
      if (scrollHeight <= clientHeight) return;

      // Scrolling down (diff < 0) when at top -> prevent body scroll
      if (scrollTop === 0 && diff < 0) {
        ev.preventDefault();
        return;
      }

      // Scrolling up (diff > 0) when at bottom -> prevent body scroll
      if (scrollTop + clientHeight >= scrollHeight - 1 && diff > 0) {
        ev.preventDefault();
        return;
      }
      // otherwise allow the element to scroll
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  return (
    // Desktop: the header's panel shell draws the card and runs the open/morph animation.
    <div
      ref={scrollRef}
      className={cn(
        "search-location-modal-scrollbar overflow-y-auto",
        isMobile ? "w-full py-2" : "max-h-[500px] w-[425px] px-6 py-5",
      )}
    >
      <LocationSearchList
        term={term}
        selectedId={filters.destination?.id}
        onLocation={handleLocation}
        onHotel={handleHotel}
        onNearby={handleNearby}
        onRecent={handleRecent}
      />
    </div>
  );
};

export default SearchDestinationModal;
